import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import PricingCards from "@/components/PricingCards";
import LaunchCountdown from "@/components/LaunchCountdown";
import {
  ArrowRight, Sparkles, Check, Wrench, GitBranch, ClipboardCheck,
  MessageSquareCode, DownloadCloud, ShieldCheck, X, Zap, Copy, Terminal,
  LineChart, PlayCircle, Star,
} from "lucide-react";

const FEATURE_ICONS = [MessageSquareCode, Sparkles, Wrench, GitBranch, DownloadCloud, ShieldCheck];

const SectionTitle = ({ title, sub, center = false }) => (
  <div className={`mb-14 ${center ? "text-center max-w-3xl mx-auto" : "max-w-2xl"}`}>
    <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium mb-4 bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">
      {title}
    </h2>
    {sub && <p className="text-zinc-400 leading-relaxed">{sub}</p>}
  </div>
);

/* ============================================================
   Product mockup — designed marketing surface, not a live editor
   ============================================================ */
function ProductMockup() {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <div
        aria-hidden
        className="absolute -inset-6 blur-3xl opacity-60 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 60% at 20% 20%, rgba(37,99,235,0.28), transparent 60%), radial-gradient(50% 50% at 80% 80%, rgba(16,185,129,0.16), transparent 60%)",
        }}
      />
      <div className="relative rounded-2xl border border-[#1F2937] bg-[#0B0C12]/90 backdrop-blur-xl shadow-[0_40px_120px_-30px_rgba(37,99,235,0.45)] overflow-hidden">
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-[#1F2937] bg-[#0C0D13]/80">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
          <span className="ml-3 text-xs font-mono text-zinc-500 truncate flex-1 min-w-0">pineforge.ai · workspace · smc_signals.pine</span>
          <span className="ml-auto shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-blue-500/10 border border-blue-500/25 text-[11px] font-mono text-blue-300">
            <Zap className="w-3 h-3" /> 8 {t("landing.mockup_credits_label")}
          </span>
        </div>

        <div className="grid lg:grid-cols-5 gap-0">
          <div className="lg:col-span-2 min-w-0 border-b lg:border-b-0 lg:border-r border-[#1F2937] p-5 flex flex-col gap-4 min-h-[380px] lg:min-h-[420px]">
            <span className="inline-flex self-start items-center gap-1.5 px-2 py-0.5 rounded-md bg-[#12141C] border border-[#1F2937] text-[10px] font-mono text-zinc-400 uppercase tracking-wider">
              {t("landing.mockup_workspace_chat")}
            </span>

            <div className="self-end max-w-[85%] rounded-2xl rounded-tr-md bg-blue-500/15 border border-blue-500/25 px-3.5 py-2.5 text-sm text-blue-50">
              {t("landing.mockup_user_msg")}
            </div>

            <div className="self-start max-w-[92%] rounded-2xl rounded-tl-md bg-[#12141C] border border-[#1F2937] px-3.5 py-2.5 text-sm text-zinc-200">
              {(() => {
                const full = t("landing.mockup_assistant_msg_html", { code: "__CODE__" });
                const parts = full.split("__CODE__");
                return (
                  <>
                    {parts[0]}
                    <span className="font-mono text-blue-300">request.security</span>
                    {parts[1] || ""}
                  </>
                );
              })()}
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/25 text-[10px] font-mono text-emerald-300">
                  <Check className="w-3 h-3" /> {t("landing.mockup_code_updated")}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-500/10 border border-blue-500/25 text-[10px] font-mono text-blue-300">
                  <GitBranch className="w-3 h-3" /> {t("landing.mockup_revision_created")}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#12141C] border border-[#1F2937] text-[10px] font-mono text-zinc-400">
                  <ClipboardCheck className="w-3 h-3 inline-block mr-0.5" />{t("landing.mockup_quality_passed_label")}
                </span>
              </div>
            </div>

            <div className="mt-auto space-y-2">
              <div className="rounded-xl border border-[#1F2937] bg-[#0C0D13] px-3.5 py-2.5 text-sm text-zinc-500">{t("landing.mockup_ask_followup")}</div>
              <p className="text-[10px] font-mono text-zinc-500 text-right">{t("landing.mockup_updates_hint")}</p>
            </div>
          </div>

          <div className="lg:col-span-3 min-w-0 p-5 flex flex-col gap-3 min-h-[380px] lg:min-h-[420px]">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono text-zinc-400">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/25 text-emerald-300">
                  <ShieldCheck className="w-3 h-3" /> {t("landing.mockup_qc_passed")}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#12141C] border border-[#1F2937]">
                  <GitBranch className="w-3 h-3 text-blue-400" /> {t("landing.mockup_rev_count")}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#12141C] border border-[#1F2937]">
                  <Wrench className="w-3 h-3 text-blue-400" /> {t("landing.mockup_repair_ready")}
                </span>
              </div>
              <div className="flex gap-1.5">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#12141C] border border-[#1F2937] text-[11px] font-mono text-zinc-300 hover:border-[#2D313F] cursor-default">
                  <Copy className="w-3 h-3" /> {t("landing.mockup_copy")}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#12141C] border border-[#1F2937] text-[11px] font-mono text-zinc-300 hover:border-[#2D313F] cursor-default">
                  <DownloadCloud className="w-3 h-3" /> .pine
                </span>
              </div>
            </div>

            <pre className="flex-1 rounded-xl border border-[#1F2937] bg-[#0A0B10] p-4 text-[11.5px] sm:text-[12.5px] font-mono text-zinc-300 leading-relaxed overflow-x-auto max-w-full whitespace-pre">
{`//@version=6
indicator("SMC Signals", overlay=true, max_boxes_count=500)

// -- inputs --
lookback = input.int(20, "Structure Lookback")
crvEntry = input.float(0.0, "CRV Entry", step=0.01)
symDXY   = input.symbol("TVC:DXY",   "DXY")
symUS10Y = input.symbol("TVC:US10Y", "US10Y")
symUS30Y = input.symbol("TVC:US30Y", "US30Y")

// -- FVG / BOS logic preserved --
bull_fvg = low > high[2] and close > open
bear_fvg = high < low[2] and close < open

// -- dashboard --
var table tbl = table.new(position.top_right, 2, 5)
if barstate.islast
    table.cell(tbl, 0, 0, "CRV",  text_color=color.gray)
    table.cell(tbl, 1, 0, str.tostring(crvEntry, "#.##"))
    table.cell(tbl, 0, 1, "DXY")
    table.cell(tbl, 1, 1, str.tostring(close))`}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const { t } = useTranslation();

  const featuresRaw = t("landing.features", { returnObjects: true });
  const features = Array.isArray(featuresRaw) ? featuresRaw : [];

  const stepsRaw = t("landing.steps", { returnObjects: true });
  const steps = Array.isArray(stepsRaw) ? stepsRaw : [];

  const compareRaw = t("landing.compare_items", { returnObjects: true });
  const compare = Array.isArray(compareRaw) ? compareRaw : [];

  const faqRaw = t("landing.faq", { returnObjects: true });
  const faq = Array.isArray(faqRaw) ? faqRaw : [];

  const repairBulletsRaw = t("landing.repair_bullets", { returnObjects: true });
  const repairBullets = Array.isArray(repairBulletsRaw) ? repairBulletsRaw : [];

  return (
    <div>
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-[#1F2937]">
        <div
          aria-hidden
          className="absolute inset-0 opacity-50 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(ellipse 80% 60% at 15% 10%, rgba(37,99,235,0.22), transparent 60%), radial-gradient(ellipse 60% 50% at 85% 90%, rgba(6,182,212,0.10), transparent 60%)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-screen"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24 lg:py-28 relative">
          {/* Phase 2.1P-DESIGN — Launch nudge: golden ★ badge lives natively
             in the Hero (moved up from the product-mockup section). It
             deep-links straight to /signup so anyone who sees the badge is
             one click away from claiming the launch offer. */}
          <Link
            to="/signup"
            data-testid="landing-workspace-star-badge"
            className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/15 transition-colors mb-6"
          >
            <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
            <span className="text-[11px] font-mono uppercase tracking-widest text-amber-300">
              {t("landing.workspace_badge_new", { defaultValue: "Open the Beam-AI Workspace" })}
            </span>
            <ArrowRight className="w-3 h-3 text-amber-400 group-hover:translate-x-0.5 transition-transform" />
          </Link>

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold mb-6 py-2 leading-tight md:leading-snug max-w-4xl min-w-0 break-words bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent"
            data-testid="landing-hero-title"
          >
            {t("landing.hero_title")}
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 mb-10 max-w-2xl leading-relaxed min-w-0 break-words">
            {t("landing.hero_subtitle")}
          </p>
          <div className="flex flex-wrap gap-3 mb-6">
            <Link to="/signup" data-testid="hero-start-btn">
              <Button className="bg-blue-600 hover:bg-blue-500 text-white h-12 px-7 text-base">
                {t("landing.cta_start")} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/pricing" data-testid="hero-pricing-btn">
              <Button
                variant="outline"
                className="border-[#1F2937] bg-[#12141C]/70 backdrop-blur hover:border-[#2D313F] hover:bg-[#12141C] text-white h-12 px-7 text-base"
              >
                {t("nav.pricing")}
              </Button>
            </Link>
          </div>
          <p className="text-xs text-zinc-500 max-w-2xl mb-8 leading-relaxed">
            {t("landing.hero_disclaimer")}
          </p>
          {/* Responsive hero check-list.
             - Uses `items-start` (not center) so long labels wrap without
               clipping the checkmark icon.
             - `text-sm md:text-base` unlocks proper mobile/tablet
               typography instead of the too-tight `text-xs`.
             - `min-w-0` on the <li> lets flexbox children wrap; the
               <span> below carries `leading-relaxed` for readable
               multi-line copy. */}
          <ul
            data-testid="landing-hero-checks"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 text-sm md:text-base text-zinc-300"
          >
            {[1, 2, 3, 4].map((n) => (
              <li
                key={n}
                data-testid={`landing-hero-check-${n}`}
                className="flex items-start gap-2 min-w-0 h-auto w-full px-1 py-1"
              >
                <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <span className="leading-relaxed break-words">
                  {t(`landing.hero_check_${n}`)}
                </span>
              </li>
            ))}
          </ul>

          {/* Phase 2.1P-FINAL — Trust metric row.
             Three glassmorphic cards sitting directly below the hero CTA
             signalling institutional-grade guarantees. Values are static
             (they describe the product's engineered contract, not live
             stats), labels/descs are i18n so DE/EN/ES/TR stay in sync. */}
          <div
            className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4"
            data-testid="landing-trust-metrics"
          >
            {["compile", "latency", "billing"].map((key) => (
              <div
                key={key}
                data-testid={`landing-metric-${key}`}
                className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl px-6 py-5 min-w-0"
              >
                <div className="text-3xl font-semibold tracking-tight bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent break-words">
                  {t(`landing.metrics.${key}_value`)}
                </div>
                <div className="mt-1 text-sm font-medium text-slate-200 break-words">
                  {t(`landing.metrics.${key}_label`)}
                </div>
                <div className="mt-2 text-xs text-zinc-500 leading-relaxed font-mono break-words">
                  {t(`landing.metrics.${key}_desc`)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRODUCT MOCKUP ============ */}
      <section className="border-b border-[#1F2937]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24">
          <SectionTitle
            title={t("landing.workspace_title")}
            sub={t("landing.workspace_sub")}
            center
          />
          <div className="max-w-6xl mx-auto" data-testid="product-mockup">
            <ProductMockup />
          </div>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24 border-b border-[#1F2937]">
        <SectionTitle title={t("landing.features_title")} />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => {
            const Icon = FEATURE_ICONS[i] || Sparkles;
            return (
              <div
                key={f.title}
                data-testid={`feature-${String(f.title).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group relative bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl p-7 hover:border-blue-500/30 hover:bg-[#09090B]/95 transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-5 group-hover:bg-blue-500/15 transition-colors">
                  <Icon className="w-5 h-5 text-blue-400" />
                </div>
                <h3 className="text-lg font-medium mb-2">{f.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ============ WORKFLOW ============ */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24 border-b border-[#1F2937]">
        <SectionTitle
          title={t("landing.workflow_title")}
          sub={t("landing.workflow_sub")}
        />
        <div className="relative">
          <div
            aria-hidden
            className="hidden lg:block absolute left-0 right-0 top-[54px] h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => {
              const num = String(i + 1).padStart(2, "0");
              return (
                <div
                  key={num}
                  data-testid={`step-${num}`}
                  className="relative bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl p-7 hover:border-[#2D313F] transition-all"
                >
                  <div className="relative z-10 inline-flex items-center gap-2 mb-6">
                    <span className="w-8 h-8 rounded-lg bg-[#0A0B10] border border-blue-500/40 flex items-center justify-center text-blue-400 text-xs font-mono">
                      {num}
                    </span>
                  </div>
                  <h3 className="text-lg font-medium mb-2">{s.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ REPAIR EXAMPLE ============ */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24 border-b border-[#1F2937]">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium mb-6 bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">{t("landing.repair_title")}</h2>
            <p className="text-zinc-400 leading-relaxed mb-6">
              {t("landing.repair_desc")}
            </p>
            <ul className="space-y-3 text-sm text-zinc-300">
              {repairBullets.map((rb) => (
                <li key={rb} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />{rb}
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-3">
            <div className="bg-amber-500/5 border border-amber-500/25 rounded-lg p-4 font-mono text-sm">
              <div className="text-amber-400 text-xs mb-2 tracking-wider">{t("landing.repair_error_label")}</div>
              <div className="text-amber-100/90">{t("landing.repair_error_msg")}</div>
            </div>
            <div className="flex justify-center py-1"><ArrowRight className="w-5 h-5 text-blue-400 rotate-90" /></div>
            <div className="bg-emerald-500/5 border border-emerald-500/25 rounded-lg p-4 font-mono text-sm">
              <div className="text-emerald-400 text-xs mb-2 tracking-wider">{t("landing.repair_fix_label")}</div>
              <div className="text-emerald-100/90">crv = input.float(0.0, &quot;CRV Entry&quot;, step=0.01)</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ COMPARISON ============ */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24 border-b border-[#1F2937]">
        <SectionTitle
          title={t("landing.compare_title")}
          sub={t("landing.compare_sub")}
          center
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl p-7">
            <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-4">{t("landing.compare_generic_label")}</p>
            <ul className="space-y-3">
              {compare.map((c) => (
                <li key={`g-${c}`} className="flex items-start gap-2 text-sm text-zinc-500">
                  <X className="w-4 h-4 text-zinc-600 mt-0.5 flex-shrink-0" />
                  <span className="line-through decoration-zinc-700">{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative bg-gradient-to-b from-blue-500/[0.08] via-transparent to-transparent border border-blue-500/30 rounded-xl p-7 shadow-[0_0_60px_-25px_rgba(37,99,235,0.5)]">
            <p className="text-xs font-mono uppercase tracking-wider text-blue-400 mb-4">{t("landing.compare_pineforge_label")}</p>
            <ul className="space-y-3">
              {compare.map((c) => (
                <li key={`u-${c}`} className="flex items-start gap-2 text-sm text-zinc-200">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============ PRICING PREVIEW ============ */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24 border-b border-[#1F2937]">
        <SectionTitle
          title={t("landing.pricing_preview_title")}
          sub={t("landing.pricing_preview_sub")}
          center
        />
        <div className="flex justify-center mb-12">
          <LaunchCountdown />
        </div>
        <PricingCards layout="landing" />
      </section>

      {/* ============ FAQ ============ */}
      <section className="max-w-3xl mx-auto px-6 py-20 sm:py-24 border-b border-[#1F2937]">
        <SectionTitle title={t("landing.faq_title")} center />
        <div className="space-y-3">
          {faq.map((f) => (
            <details
              key={f.q}
              data-testid={`faq-${String(f.q).slice(0, 24).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-lg p-6 open:border-[#2D313F]"
            >
              <summary className="flex justify-between items-center cursor-pointer list-none gap-4">
                <span className="text-white font-medium">{f.q}</span>
                <span className="text-blue-400 group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
              </summary>
              <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(ellipse 70% 50% at 50% 100%, rgba(37,99,235,0.3), transparent 60%)",
          }}
        />
        <div className="max-w-4xl mx-auto px-6 py-24 sm:py-28 text-center relative">
          <div className="inline-flex items-center gap-2 mb-6 text-blue-400">
            <LineChart className="w-6 h-6" />
            <PlayCircle className="w-6 h-6" />
            <Terminal className="w-6 h-6" />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium mb-4 bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">{t("landing.final_cta_title")}</h2>
          <p className="text-zinc-400 mb-10 max-w-lg mx-auto">
            {t("landing.final_cta_sub")}
          </p>
          <Link to="/signup" data-testid="cta-signup">
            <Button className="bg-blue-600 hover:bg-blue-500 text-white h-12 px-8 text-base">
              {t("landing.final_cta_btn")} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* ============ FOOTER / DISCLAIMER ============ */}
      <footer className="border-t border-[#1F2937] py-12" data-testid="landing-footer">
        <div className="max-w-4xl mx-auto px-6 text-xs text-zinc-500 leading-relaxed space-y-3">
          <p>{t("landing.footer_disclaimer_1")}</p>
          <p>{t("landing.footer_disclaimer_2")}</p>
          <div className="flex flex-wrap gap-4 justify-between items-center pt-4 border-t border-[#1F2937] font-mono">
            <span>© PineForge.AI</span>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              <Link to="/pricing" className="hover:text-white transition-colors">{t("nav.pricing")}</Link>
              <Link to="/login" className="hover:text-white transition-colors">{t("nav.login")}</Link>
              <Link to="/signup" className="hover:text-white transition-colors">{t("nav.signup")}</Link>
              <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">AGB</Link>
              <Link to="/imprint" className="hover:text-white transition-colors" data-testid="footer-imprint">Impressum</Link>
              <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Datenschutz</Link>
              <Link to="/cancellation" className="hover:text-white transition-colors" data-testid="footer-cancellation">Widerruf</Link>
              <Link to="/cancellation-form" className="hover:text-white transition-colors" data-testid="footer-cancellation-form">Widerrufsformular</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
